import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: token + new password
  const [email, setEmail] = useState('');
  const [resetData, setResetData] = useState({ token: '', new_password: '' });
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('Si el email existe, recibirás un código en tu casilla de correo');
      setStep(2);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al solicitar recuperación');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, resetData);
      toast.success('Contraseña restablecida exitosamente');
      navigate('/');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-white/95 backdrop-blur-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Lock size={48} className="text-purple-600" />
            </div>
            <CardTitle className="text-2xl">Recuperar Contraseña</CardTitle>
            <CardDescription>
              {step === 1
                ? 'Ingresá tu email para recibir el código de recuperación'
                : 'Ingresá el código y tu nueva contraseña'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="pl-10"
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={loading}
                  data-testid="request-reset-button"
                >
                  {loading ? 'Enviando...' : 'Enviar Código'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-semibold text-blue-800 mb-2">
                    📧 Revisa tu casilla de correo
                  </p>
                  <p className="text-xs text-blue-700">
                    Si el email existe, recibirás un mensaje con el código de recuperación.
                    Copia el código del email y pégalo aquí abajo.
                  </p>
                </div>
                <div>
                  <Label htmlFor="token">Código de Recuperación</Label>
                  <Input
                    id="token"
                    value={resetData.token}
                    onChange={(e) => setResetData({ ...resetData, token: e.target.value })}
                    placeholder="Pega el código del email aquí"
                    required
                    data-testid="reset-token"
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El código fue enviado a tu email. Revisa también la carpeta de spam.
                  </p>
                </div>
                <div>
                  <Label htmlFor="new-password">Nueva Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="new-password"
                      type="password"
                      value={resetData.new_password}
                      onChange={(e) => setResetData({ ...resetData, new_password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10"
                      required
                      data-testid="new-password"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={loading}
                  data-testid="reset-password-button"
                >
                  {loading ? 'Restableciendo...' : 'Restablecer Contraseña'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-600"
                data-testid="back-to-login"
              >
                <ArrowLeft className="mr-2" size={16} />
                Volver al inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
